import React, { useState, useEffect } from 'react';
import { Download, X, Share, PlusSquare, BellRing, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const IOS_STEPS = [
  { icon: Share, title: 'Tap the Share button', desc: 'It\'s the square with the arrow at the bottom of Safari (or top on iPad).' },
  { icon: PlusSquare, title: 'Tap "Add to Home Screen"', desc: 'Scroll down the share menu until you see it, then tap it.' },
  { icon: BellRing, title: 'Open InnerGlow from your Home Screen', desc: 'Launch it like an app — you\'ll get daily reminders & 1-tap logging.' },
];

const IOSGuideModal = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-sm rounded-2xl bg-white" data-testid="ios-install-guide">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl text-[#1A1514]">Add InnerGlow to your Home Screen</DialogTitle>
        <DialogDescription className="text-sm text-[#8C7A76]">
          3 quick steps to unlock lock-screen reminders and 1-tap daily logging.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 mt-1">
        {IOS_STEPS.map((s, i) => (
          <div key={s.title} className="flex items-start gap-3" data-testid={`ios-guide-step-${i + 1}`}>
            <div className="w-9 h-9 rounded-xl bg-[#FF6B4A]/10 text-[#FF6B4A] flex items-center justify-center flex-shrink-0">
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-bold text-[#1A1514]">{i + 1}. {s.title}</p>
              <p className="text-xs text-[#8C7A76] mt-0.5">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-[#8C7A76] bg-[#FFF2EC] rounded-lg px-3 py-2 mt-2">
        Apple doesn't let websites install themselves — these steps take about 10 seconds.
      </p>
    </DialogContent>
  </Dialog>
);

const InstallAppBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) return;

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // iOS detection
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    if (ios) {
      // Show iOS install instructions after a delay
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }

    // Android/Chrome: listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    setShowBanner(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-0 sm:bottom-6 sm:left-auto sm:right-6 sm:w-80" data-testid="install-app-banner">
        <div
          className={`bg-[#1A1514] text-white rounded-2xl p-4 shadow-2xl flex items-start gap-3 ${isIOS ? 'cursor-pointer active:scale-[0.98] transition-transform' : ''}`}
          onClick={isIOS ? () => setShowGuide(true) : undefined}
          data-testid={isIOS ? 'ios-install-banner-tap' : 'install-banner-body'}
        >
          <div className="w-10 h-10 bg-[#FF6B4A] rounded-xl flex items-center justify-center flex-shrink-0">
            <Download className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-sm">{isIOS ? 'Enable lock-screen reminders' : 'Install InnerGlow'}</h3>
            {isIOS ? (
              <>
                <p className="text-xs text-white/70 mt-1">
                  Add InnerGlow to your Home Screen to get daily reminders &amp; 1-tap logging.
                </p>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowGuide(true); }}
                  className="mt-2 bg-[#4ADE80] text-[#1A1514] text-xs font-semibold px-4 py-1.5 rounded-full inline-flex items-center gap-1 active:scale-95 transition-transform"
                  data-testid="ios-show-me-how-btn"
                >
                  Show me how <ChevronRight className="w-3 h-3" />
                </button>
              </>
            ) : (
              <>
                <p className="text-xs text-white/70 mt-1">Add to your home screen for reminders &amp; 1-tap daily logging</p>
                <button
                  onClick={handleInstall}
                  className="mt-2 bg-[#4ADE80] hover:bg-[#3cbf6a] text-[#1A1514] text-xs font-semibold px-4 py-1.5 rounded-full transition-colors"
                  data-testid="install-app-btn"
                >
                  Install Now
                </button>
              </>
            )}
          </div>
          <button onClick={handleDismiss} className="text-white/50 hover:text-white" data-testid="dismiss-install-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <IOSGuideModal open={showGuide} onOpenChange={setShowGuide} />
    </>
  );
};

export default InstallAppBanner;
